import { Task } from '../../services/api/tasksService';

export const lifecycleOrder = [
  'READY',
  'IN_PROGRESS',
  'PAUSED',
  'INSPECTION_PENDING',
  'INSPECTION_FAILED',
  'BLOCKED',
  'SCHEDULED',
  'WAITING',
];

export const lifecycleLabels: Record<string, string> = {
  READY: 'Ready',
  IN_PROGRESS: 'In progress',
  PAUSED: 'Paused',
  INSPECTION_PENDING: 'Inspection pending',
  INSPECTION_FAILED: 'Needs rework',
  BLOCKED: 'Blocked',
  SCHEDULED: 'Scheduled',
  WAITING: 'Waiting',
};

export const normalizeStatus = (status?: string | null) => String(status || 'DRAFT').toUpperCase();

export const canStartTask = (task: Pick<Task, 'status' | 'lifecycleStatus'>) =>
  ['READY', 'PAUSED', 'INSPECTION_FAILED'].includes(normalizeStatus((task as Task).workerLifecycleStatus ?? task.lifecycleStatus ?? task.status));

const currentTaskStatusOrder = ['IN_PROGRESS', 'PAUSED', 'INSPECTION_FAILED', 'READY'];

const taskDateValue = (task: Task) => {
  const value = task.updatedAt ?? task.createdAt ?? task.dueDate ?? task.startDate ?? '';
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

export const selectCurrentWorkerTask = (tasks: Task[]) =>
  [...tasks]
    .filter((task) => currentTaskStatusOrder.includes(normalizeStatus(task.workerLifecycleStatus ?? task.lifecycleStatus ?? task.status)))
    .sort((a, b) => {
      const statusDelta = currentTaskStatusOrder.indexOf(normalizeStatus(a.workerLifecycleStatus ?? a.lifecycleStatus ?? a.status))
        - currentTaskStatusOrder.indexOf(normalizeStatus(b.workerLifecycleStatus ?? b.lifecycleStatus ?? b.status));
      if (statusDelta !== 0) return statusDelta;
      return taskDateValue(b) - taskDateValue(a);
    })[0] ?? null;

export const taskTeamIds = (task: Task): string[] => {
  const ids = new Set<string>();
  if (task.teamId) ids.add(task.teamId);
  if (task.team?.id) ids.add(task.team.id);
  const assignments = (task as any).teamAssignments ?? (task as any).team_assignments ?? [];
  if (Array.isArray(assignments)) {
    assignments.forEach((assignment) => {
      const id = assignment.teamId ?? assignment.team_id ?? assignment.team?.id;
      if (id) ids.add(id);
    });
  }
  return Array.from(ids);
};

export const filterTeamTasks = (tasks: Task[], teamId?: string) =>
  teamId ? tasks.filter((task) => taskTeamIds(task).includes(teamId)) : tasks;

export const groupTasksByLifecycle = (tasks: Task[]) => {
  const groups = new Map<string, Task[]>();
  tasks.forEach((task) => {
    const status = normalizeStatus(task.workerLifecycleStatus ?? task.lifecycleStatus ?? task.status);
    groups.set(status, [...(groups.get(status) ?? []), task]);
  });
  return lifecycleOrder
    .filter((status) => groups.has(status))
    .map((status) => ({ status, tasks: groups.get(status) ?? [] }));
};
